import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useMemo, useRef, useState } from "react";
import { getAudioContext, resumeAudio, unlockAudio, audioFailureReason } from "@/lib/audio-context";
import { claimAudio, registerAudioOwner, type AudioOwner } from "@/lib/audio-bus";
import { useAudioPlayerState } from "@/components/GlobalMiniPlayer";
import { playTrack, pause as pausePlayer, stop as stopPlayer, getState as getPlayerState } from "@/lib/audio-player";

import {
  RECORDED_PREFIX,
  SOUNDS,
  CATEGORIES,
  useSoundManager,
  useBackgroundKeepAlive,
  FAV_KEY,
  MIXES_KEY,
  lsGet,
  lsSet,
  type SoundDef,
  type Category,
  type Mix,
} from "@/lib/sounds-library";
import medUrgeSurfingAsset from "@/assets/med-urge-surfing.mp3.asset.json";
import {
  Music,
  Play,
  Pause,
  Waves,
  CloudRain,
  CloudLightning,
  Moon,
  Heart,
  Sparkles,
  Headphones,
  Volume2,
  VolumeX,
  Wind,
  Trees,
  Flame,
  Bird,
  Droplets,
  Bell,
  Star,
  Sun,
  Timer,
  Square,
  Activity,
  Radio,
  Piano,
  ListMusic,
  Plus,
  Trash2,
  Layers,
  EyeOff,
  SkipBack,
  SkipForward,
  Repeat,
  Search,
  X,
} from "lucide-react";
import { useComfortPrefs, toggleHiddenSound } from "@/lib/preferences";

export const Route = createFileRoute("/sounds")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Sounds & Meditations Library — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Trauma-informed soundscapes for sleep, focus, grounding, and nervous system regulation. Mix sounds, save mixes, sleep timer, background playback.",
      },
      { property: "og:title", content: "Sounds & Meditations Library — Recovery Trauma™" },
      { property: "og:description", content: "Trauma-informed soundscapes for sleep, focus, grounding, and nervous system regulation. Mix sounds, save mixes, sleep timer, background playback." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/sounds" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/sounds" }],
  }),
  component: withMemberGate(SoundsPage, "Sounds"),
});

// --- Sound definitions ------------------------------------------------------


// --- UI ---------------------------------------------------------------------

function SoundCard({
  sound, isPlaying, onToggle, onStop, onNext, onPrev, hasNeighbours,
  isFav, onFav, onHide, volume, onVolume,
  timerRemaining, timerTotal, onStartTimer, onCancelTimer,
}: {
  sound: SoundDef;
  isPlaying: boolean;
  onToggle: () => void;
  onStop: () => void;
  onNext: () => void;
  onPrev: () => void;
  hasNeighbours: boolean;
  isFav: boolean;
  onFav: () => void;
  onHide: () => void;
  volume: number;
  onVolume: (v: number) => void;
  timerRemaining: number | null;
  timerTotal: number | null;
  onStartTimer: (m: number) => void;
  onCancelTimer: () => void;
}) {
  const Icon = sound.icon;
  const disabled = !!sound.comingSoon;

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  const showTimer = isPlaying && timerRemaining !== null && timerTotal !== null;
  const elapsed = showTimer ? (timerTotal! - timerRemaining!) : 0;
  const progressPct = showTimer ? Math.min(100, Math.max(0, (elapsed / timerTotal!) * 100)) : 0;

  return (
    <div className={`group relative overflow-hidden rounded-2xl border bg-card p-5 shadow-sm transition-all hover:shadow-[var(--shadow-gold)] ${isPlaying ? "border-[var(--gold)]" : "border-border/60"} ${disabled ? "opacity-70" : ""}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-gold-light to-gold text-foreground/80 shadow-inner">
            <Icon className="h-5 w-5" />
          </div>
          <span className="text-2xl" aria-hidden>{sound.emoji}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onFav}
            aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
            className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:text-[var(--gold-deep)] sm:h-9 sm:w-9"
          >
            <Heart className={`h-4 w-4 ${isFav ? "fill-[var(--gold-deep)] text-[var(--gold-deep)]" : ""}`} />
          </button>
          <button
            onClick={onToggle}
            disabled={disabled}
            aria-label={disabled ? "Coming soon" : (isPlaying ? "Pause" : "Play")}
            className={`flex h-11 w-11 items-center justify-center rounded-full transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 ${isPlaying ? "bg-[var(--gold-deep)] text-background" : "bg-foreground text-background"}`}
          >
            {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-[1px]" />}
          </button>
        </div>
      </div>
      <h3 className="font-display text-xl text-foreground">{sound.title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{sound.blurb}</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {sound.minutes ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-xs text-muted-foreground">
            {sound.minutes} min {sound.playOnce ? "· guided voice" : "· recording"}
          </span>
        ) : null}
        {sound.headphones && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Headphones className="h-3 w-3" /> Headphones recommended
          </span>
        )}
      </div>


      {/* Transport row — Prev · Stop · Next · Loop */}
      <div className="mt-4 flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={disabled || !hasNeighbours}
          aria-label="Previous sound"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          <SkipBack className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onStop}
          disabled={disabled || !isPlaying}
          aria-label="Stop"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Square className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={disabled || !hasNeighbours}
          aria-label="Next sound"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          <SkipForward className="h-4 w-4" />
        </button>
        <span
          aria-label="Loops continuously"
          title="Loops continuously"
          className="ms-1 inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-1 text-[11px] text-muted-foreground"
        >
          <Repeat className="h-3 w-3" /> Loop
        </span>
      </div>

      {/* Per-sound volume — always visible so members can pre-set or adjust on the fly */}
      <div className={`mt-3 flex items-center gap-2 rounded-xl border border-border/60 bg-background/60 px-3 py-2 ${isPlaying ? "" : "opacity-80"}`}>
        <Volume2 className="h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="range" min={0} max={1} step={0.01}
          value={volume}
          onChange={(e) => onVolume(parseFloat(e.target.value))}
          disabled={disabled}
          className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-muted accent-[var(--gold-deep)] disabled:cursor-not-allowed"
          aria-label={`Volume for ${sound.title}`}
        />
        <span className="w-8 text-end font-mono text-[11px] text-muted-foreground">
          {Math.round(volume * 100)}
        </span>
      </div>

      {/* Sleep timer */}
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-border/60 bg-background/60 px-3 py-2">
        <Timer className="h-3.5 w-3.5 text-muted-foreground" />
        {timerRemaining !== null ? (
          <>
            <span className="font-mono text-xs text-foreground">{fmt(timerRemaining)}</span>
            <button
              type="button"
              onClick={onCancelTimer}
              className="ms-auto rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="text-[11px] text-muted-foreground">Sleep timer</span>
            <div className="ms-auto flex items-center gap-1">
              {[15, 30, 60, 90].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => onStartTimer(m)}
                  disabled={disabled || !isPlaying}
                  className="rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {m}m
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Progress bar (sleep-timer elapsed/remaining when active) */}
      {showTimer && (
        <div className="mt-3">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-[var(--gold-deep)] transition-[width] duration-1000 ease-linear"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-1 flex justify-between font-mono text-[10px] text-muted-foreground">
            <span>{fmt(elapsed)} elapsed</span>
            <span>{fmt(timerRemaining!)} left</span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onHide}
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition hover:text-foreground"
        aria-label={`Hide ${sound.title} from this library`}
        title="Don't show this again"
      >
        <EyeOff className="h-3 w-3" /> Don't show this
      </button>

      {isPlaying && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1 overflow-hidden">
          <div className="h-full w-full animate-pulse bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent" />
        </div>
      )}
    </div>
  );
}

type SoundGroup = "calming" | "sleep" | "bilateral" | "soundbath";

function groupOf(s: SoundDef): SoundGroup {
  if (s.category === "Sound Bath") return "soundbath";
  if (s.bilateral) return "bilateral";
  // Noise + Sleep => Sleep & Relaxation. Everything else => Calming Sounds.
  if (s.category === "Sleep" || s.category === "Noise") return "sleep";
  return "calming";
}

type View = "calming" | "sleep" | "bilateral" | "soundbath" | "favorites";


function SoundsPage() {
  const mgr = useSoundManager();
  const [favs, setFavs] = useState<string[]>([]);
  const [mixes, setMixes] = useState<Mix[]>([]);
  const [view, setView] = useState<View>("calming");
  const [savingMix, setSavingMix] = useState(false);
  const [newMixName, setNewMixName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeChip, setActiveChip] = useState<string | null>(null);

  useBackgroundKeepAlive(
    mgr.playingSlugs.length > 0,
    mgr.playingSlugs.length === 1
      ? (SOUNDS.find((s) => s.slug === mgr.playingSlugs[0])?.title ?? "Sounds & Meditations")
      : `Mix · ${mgr.playingSlugs.length} sounds`,
  );

  useEffect(() => {
    setFavs(lsGet(FAV_KEY, [] as string[]));
    setMixes(lsGet(MIXES_KEY, [] as Mix[]));
  }, []);

  const toggleFav = (slug: string) => {
    setFavs((prev) => {
      const next = prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug];
      lsSet(FAV_KEY, next);
      return next;
    });
    const def = SOUNDS.find((s) => s.slug === slug);
    if (def) {
      import("@/lib/favorites").then(({ toggleFavorite }) => {
        toggleFavorite("rt:fav-sounds", {
          id: def.slug,
          label: `${def.emoji} ${def.title}`,
          sub: def.category,
          to: "/sounds",
        });
      });
    }
  };

  const comfort = useComfortPrefs();
  const visibleSounds = useMemo(
    () => SOUNDS.filter((s) => !comfort.hiddenSounds.includes(s.slug)),
    [comfort.hiddenSounds],
  );

  const calmingCount = useMemo(() => visibleSounds.filter((s) => groupOf(s) === "calming").length, [visibleSounds]);
  const sleepCount = useMemo(() => visibleSounds.filter((s) => groupOf(s) === "sleep").length, [visibleSounds]);
  const bilateralCount = useMemo(() => visibleSounds.filter((s) => groupOf(s) === "bilateral").length, [visibleSounds]);
  const soundBathCount = useMemo(() => visibleSounds.filter((s) => groupOf(s) === "soundbath").length, [visibleSounds]);


  const filtered = useMemo(() => {
    let base = view === "favorites"
      ? visibleSounds.filter((s) => favs.includes(s.slug))
      : visibleSounds.filter((s) => groupOf(s) === view);

    if (view === "calming") {
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        base = base.filter((s) => {
          const hay = `${s.title} ${s.blurb} ${s.category} ${s.kind ?? ""}`.toLowerCase();
          return hay.includes(q);
        });
      }
      if (activeChip) {
        const chip = activeChip.toLowerCase();
        base = base.filter((s) => {
          if (chip === "favourites") return favs.includes(s.slug);
          const hay = `${s.title} ${s.blurb} ${s.category} ${s.kind ?? ""}`.toLowerCase();
          if (chip === "nature") {
            return /rain|ocean|forest|bird|wind|water|nature|storm|thunder|creek|river|meadow/.test(hay);
          }
          return hay.includes(chip);
        });
      }
    }
    return base;
  }, [view, favs, visibleSounds, searchQuery, activeChip]);


  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  const saveCurrentAsMix = () => {
    const name = newMixName.trim() || `Mix ${mixes.length + 1}`;
    const slugs = [...mgr.playingSlugs];
    if (slugs.length === 0) return;
    const volumes: Record<string, number> = {};
    slugs.forEach((s) => { volumes[s] = mgr.perSoundVolume[s] ?? 0.8; });
    const next: Mix[] = [...mixes, { id: crypto.randomUUID(), name, slugs, volumes }];
    setMixes(next);
    lsSet(MIXES_KEY, next);
    setSavingMix(false);
    setNewMixName("");
  };

  const deleteMix = (id: string) => {
    const next = mixes.filter((m) => m.id !== id);
    setMixes(next);
    lsSet(MIXES_KEY, next);
  };

  const playingCount = mgr.playingSlugs.length;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <header className="mb-6 text-center sm:mb-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[11px] text-muted-foreground">
          <Music className="h-3.5 w-3.5" /> Sound Library
        </div>
        <h1 className="font-display text-3xl tracking-tight text-foreground sm:text-5xl md:text-6xl">
          Sound Library
        </h1>
        <p className="mx-auto mt-2 hidden max-w-xl text-base leading-relaxed text-muted-foreground sm:block">
          Guided meditations, calming sounds, sleep &amp; relaxation, and bilateral audio —
          choose what you need right now.
        </p>
      </header>

      {mgr.audioError && (
        <div
          role="status"
          className="mb-4 flex items-start gap-3 rounded-2xl border border-rose-300/60 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-400/30 dark:bg-rose-950/40 dark:text-rose-100"
        >
          <VolumeX className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">Sound didn&rsquo;t start</p>
            <p className="mt-0.5 leading-relaxed opacity-90">{mgr.audioError}</p>
          </div>
          <button
            type="button"
            onClick={mgr.clearAudioError}
            className="shrink-0 rounded-full px-2 py-1 text-xs font-medium underline underline-offset-2"
          >
            Dismiss
          </button>
        </div>
      )}



      <div className="mb-4 flex justify-end">
        <Link
          to="/preferences"
          className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border/60 bg-card px-3 py-1.5 text-[11px] font-medium text-muted-foreground transition hover:text-foreground"
        >
          🛡️ Comfort settings
          {comfort.hiddenSounds.length > 0 && (
            <span className="rounded-full bg-foreground px-1.5 py-px text-[10px] text-background">
              {comfort.hiddenSounds.length} hidden
            </span>
          )}
        </Link>
      </div>


      {/* Three primary paths */}
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Link
          to="/meditations"
          className="group flex items-center gap-4 rounded-2xl border border-border/60 bg-gradient-to-br from-card via-card to-blush/20 p-4 transition hover:-translate-y-0.5 hover:border-[var(--gold-deep)]/60 hover:shadow-[var(--shadow-gold)] sm:flex-col sm:items-start sm:p-5"
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[var(--gradient-gold)] text-2xl shadow-[var(--shadow-gold)]">
            🎧
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-base text-foreground sm:text-lg">Guided Meditations</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">Voice-led · Anxiety, sleep, inner child & more</p>
          </div>
          <span className="hidden text-[11px] font-semibold text-[var(--gold-deep)] sm:inline">Open →</span>
        </Link>

        <button
          type="button"
          onClick={() => { setView("calming"); }}
          aria-pressed={view === "calming"}
          className={`group flex items-center gap-4 rounded-2xl border p-4 text-start transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-gold)] sm:flex-col sm:items-start sm:p-5 ${
            view === "calming"
              ? "border-[var(--gold-deep)]/60 bg-gradient-to-br from-card to-gold/10"
              : "border-border/60 bg-card hover:border-[var(--gold-deep)]/60"
          }`}
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-100/60 text-2xl">
            🌿
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-base text-foreground sm:text-lg">Calming Sounds</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">{calmingCount} sounds · Rain, ocean, forest, fire</p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => { setView("sleep"); }}
          aria-pressed={view === "sleep"}
          className={`group flex items-center gap-4 rounded-2xl border p-4 text-start transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-gold)] sm:flex-col sm:items-start sm:p-5 ${
            view === "sleep"
              ? "border-[var(--gold-deep)]/60 bg-gradient-to-br from-card to-indigo-100/30"
              : "border-border/60 bg-card hover:border-[var(--gold-deep)]/60"
          }`}
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-indigo-100/60 text-2xl">
            🌙
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-base text-foreground sm:text-lg">Sleep & Relaxation</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">{sleepCount} sounds · Deep sleep, brown noise, drones</p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => { setView("bilateral"); }}
          aria-pressed={view === "bilateral"}
          className={`group flex items-center gap-4 rounded-2xl border p-4 text-start transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-gold)] sm:flex-col sm:items-start sm:p-5 ${
            view === "bilateral"
              ? "border-[var(--gold-deep)]/60 bg-gradient-to-br from-card to-sky-100/30"
              : "border-border/60 bg-card hover:border-[var(--gold-deep)]/60"
          }`}
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-sky-100/60 text-2xl">
            🎧
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-base text-foreground sm:text-lg">Bilateral Audio</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">{bilateralCount} tracks · Alternating left–right listening</p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => { setView("soundbath"); }}
          aria-pressed={view === "soundbath"}
          className={`group flex items-center gap-4 rounded-2xl border p-4 text-start transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-gold)] sm:flex-col sm:items-start sm:p-5 ${
            view === "soundbath"
              ? "border-[var(--gold-deep)]/60 bg-gradient-to-br from-card to-amber-100/30"
              : "border-border/60 bg-card hover:border-[var(--gold-deep)]/60"
          }`}
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-amber-100/60 text-2xl">
            🎐
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-base text-foreground sm:text-lg">Sound Baths</h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">{soundBathCount} sessions · Bowls, resonance, long immersive rest</p>
          </div>
        </button>
      </div>



      {/* Favourites pill */}
      <div className="mb-5 flex items-center justify-center">
        <button
          type="button"
          onClick={() => setView("favorites")}
          aria-pressed={view === "favorites"}
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[12px] font-semibold transition ${
            view === "favorites"
              ? "border-[var(--gold-deep)] bg-foreground text-background"
              : "border-border/60 bg-card text-muted-foreground hover:text-foreground"
          }`}
        >
          ❤️ My Favourites{favs.length ? ` (${favs.length})` : ""}
        </button>
      </div>



      {/* My Mixes */}
      {(mixes.length > 0 || playingCount > 0) && (
        <section className="mb-8 rounded-2xl border border-border/60 bg-card p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-display text-lg text-foreground">
              <ListMusic className="h-4 w-4" /> My Mixes
            </h2>
            {playingCount > 0 && !savingMix && (
              <button
                onClick={() => setSavingMix(true)}
                className="flex items-center gap-1 rounded-full bg-foreground px-3 py-1.5 text-xs text-background transition-transform hover:scale-105"
              >
                <Plus className="h-3.5 w-3.5" /> Save current mix ({playingCount})
              </button>
            )}
          </div>
          {savingMix && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <input
                autoFocus
                value={newMixName}
                onChange={(e) => setNewMixName(e.target.value)}
                placeholder="Name this mix (e.g. Bedtime cocoon)"
                className="flex-1 min-w-[200px] rounded-full border border-border/60 bg-background px-4 py-2 text-sm outline-none focus:border-[var(--gold-deep)]"
                onKeyDown={(e) => { if (e.key === "Enter") saveCurrentAsMix(); }}
              />
              <button onClick={saveCurrentAsMix} className="rounded-full bg-[var(--gold-deep)] px-4 py-2 text-xs text-background">Save</button>
              <button onClick={() => { setSavingMix(false); setNewMixName(""); }} className="rounded-full border border-border/60 px-4 py-2 text-xs text-muted-foreground">Cancel</button>
            </div>
          )}
          {mixes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Layer a few sounds, then save them as a named mix to play later in one tap.
            </p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {mixes.map((mix) => (
                <div key={mix.id} className="flex items-center justify-between gap-2 rounded-xl border border-border/60 bg-background/60 p-3">
                  <button onClick={() => mgr.playMix(mix)} className="flex flex-1 items-center gap-2 text-start">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background">
                      <Play className="h-4 w-4 translate-x-[1px]" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-display text-sm text-foreground">{mix.name}</p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {mix.slugs.map((s) => SOUNDS.find((x) => x.slug === s)?.emoji ?? "•").join(" ")} · {mix.slugs.length} sounds
                      </p>
                    </div>
                  </button>
                  <button onClick={() => deleteMix(mix.id)} aria-label="Delete mix" className="rounded-full p-2 text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Active section heading */}
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="font-display text-lg text-foreground sm:text-xl">
          {view === "calming" && "🌿 Calming Sounds"}
          {view === "sleep" && "🌙 Sleep & Relaxation"}
          {view === "bilateral" && "🎧 Bilateral Audio"}
          {view === "soundbath" && "🎐 Sound Baths"}

          {view === "favorites" && "❤️ My Favourites"}
        </h2>
        <span className="text-[11px] text-muted-foreground">{filtered.length} tracks</span>
      </div>

      {view === "calming" && (
        <div className="mb-5 space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search rain, ocean, forest, fire…"
              aria-label="Search calming sounds"
              className="w-full rounded-full border border-border/60 bg-card py-2.5 ps-9 pe-9 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-[var(--gold-deep)] focus:ring-2 focus:ring-[var(--gold-deep)]/20"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                className="absolute end-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {["Nature", "Sleep", "Water", "Rain", "Forest", "Fire", "Favourites"].map((chip) => {
              const active = activeChip === chip;
              return (
                <button
                  key={chip}
                  type="button"
                  onClick={() => setActiveChip(active ? null : chip)}
                  aria-pressed={active}
                  className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition ${
                    active
                      ? "border-[var(--gold-deep)] bg-foreground text-background"
                      : "border-border/60 bg-card text-muted-foreground hover:text-foreground hover:border-[var(--gold-deep)]/60"
                  }`}
                >
                  {chip}
                </button>
              );
            })}
            {(activeChip || searchQuery) && (
              <button
                type="button"
                onClick={() => { setActiveChip(null); setSearchQuery(""); }}
                className="rounded-full px-3 py-1.5 text-[12px] font-medium text-muted-foreground underline-offset-2 hover:underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {view === "bilateral" && (
        <div className="mb-5 rounded-2xl border border-sky-200/50 bg-sky-50/40 p-4 text-[12px] leading-relaxed text-foreground/80">
          <p className="font-semibold text-foreground">🎧 Best experienced with headphones.</p>
          <p className="mt-1">
            Bilateral audio uses alternating sounds between the left and right ear.
            Some people find this rhythm calming, grounding, or supportive for relaxation and focus.
          </p>
          <p className="mt-1 text-muted-foreground">
            For wellbeing and self-reflection only — not a substitute for therapy, EMDR, medical care, or treatment.
          </p>
        </div>
      )}


      {filtered.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          {view === "favorites"
            ? "No favourites yet. Tap the heart on any sound to save it here."
            : "Nothing here yet."}
        </p>

      ) : (
        <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:snap-none sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-3">
          {filtered.map((s, idx) => {
            const isThisPlaying = mgr.playingSlugs.includes(s.slug);
            const goTo = (target: SoundDef) => {
              if (target.slug === s.slug) return;
              if (isThisPlaying) mgr.toggleOne(s);
              if (!target.comingSoon) mgr.toggleOne(target);
            };
            const prevIdx = (idx - 1 + filtered.length) % filtered.length;
            const nextIdx = (idx + 1) % filtered.length;
            return (
              <div
                key={s.slug}
                className="w-[82vw] max-w-[340px] shrink-0 snap-start sm:w-auto sm:max-w-none sm:shrink"
              >
              <SoundCard
                sound={s}
                isPlaying={isThisPlaying}
                isFav={favs.includes(s.slug)}
                onFav={() => toggleFav(s.slug)}
                onToggle={() => mgr.toggleOne(s)}
                onStop={() => { if (isThisPlaying) mgr.stopOne(s.slug); }}
                onPrev={() => goTo(filtered[prevIdx])}
                onNext={() => goTo(filtered[nextIdx])}
                hasNeighbours={filtered.length > 1}
                onHide={() => {
                  if (mgr.playingSlugs.includes(s.slug)) mgr.toggleOne(s);
                  toggleHiddenSound(s.slug);
                }}
                volume={mgr.perSoundVolume[s.slug] ?? 0.8}
                onVolume={(v) => mgr.setSoundVolume(s.slug, v)}
                timerRemaining={isThisPlaying ? mgr.timerRemaining : null}
                timerTotal={isThisPlaying && mgr.timerMinutes !== null ? mgr.timerMinutes * 60 : null}
                onStartTimer={(m) => mgr.startTimer(m)}
                onCancelTimer={mgr.cancelTimer}
              />
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-6 text-center text-xs text-muted-foreground">
        Generated locally in your browser — no downloads, no tracking. All sounds fade in and out smoothly.
        If a sound feels activating, lower its volume or pause it; what soothes one nervous system may unsettle another.
      </p>

      {/* Bottom player bar — shows when at least one sound is playing */}
      {playingCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 backdrop-blur">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-gold-light to-gold">
                <Layers className="h-4 w-4 text-foreground/80" />
              </span>
              <div className="min-w-0">
                <p className="truncate font-display text-base text-foreground">
                  {playingCount === 1
                    ? SOUNDS.find((s) => s.slug === mgr.playingSlugs[0])?.title
                    : `Mixing ${playingCount} sounds`}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {mgr.playingSlugs.map((s) => SOUNDS.find((x) => x.slug === s)?.emoji ?? "•").join(" ")}
                </p>
              </div>
            </div>

            <div className="flex flex-1 items-center gap-3 min-w-[180px]">
              {mgr.masterVolume === 0 ? <VolumeX className="h-4 w-4 text-muted-foreground" /> : <Volume2 className="h-4 w-4 text-muted-foreground" />}
              <input
                type="range" min={0} max={1} step={0.01}
                value={mgr.masterVolume}
                onChange={(e) => mgr.setMasterVolume(parseFloat(e.target.value))}
                className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-muted accent-[var(--gold-deep)]"
                aria-label="Master volume"
              />
            </div>

            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4 text-muted-foreground" />
              {mgr.timerRemaining !== null ? (
                <>
                  <span className="font-mono text-sm text-foreground">{fmt(mgr.timerRemaining)}</span>
                  <button onClick={mgr.cancelTimer} className="rounded-full border border-border/60 px-2 py-1 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                </>
              ) : (
                <div className="flex items-center gap-1">
                  {[15, 30, 60, 90].map((m) => (
                    <button
                      key={m}
                      onClick={() => mgr.startTimer(m)}
                      className="rounded-full border border-border/60 px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      {m}m
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={mgr.stopAll}
              aria-label="Stop all"
              className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-foreground text-background transition-transform hover:scale-105 sm:h-10 sm:w-10"
            >
              <Square className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
